import React from "react";
import { Button } from "../../components/ui/button";
import { CalendarRange } from "lucide-react";
import { useTradeJournal } from "../../context/TradeJournalContext";
import { TimeFilter, DateRange } from "../../types/tradingJournal";
import { cn } from "../../lib/utils";
import DateRangePicker from "./DateRangePicker";
import { endOfDay } from "date-fns";
import { useIsMobile } from "../../hooks/use-mobile";

interface DateRangeSelectorProps {
  className?: string;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ className }) => {
  const { timeFilter, setTimeFilter, customDateRange, setCustomDateRange } =
    useTradeJournal();
  const isMobile = useIsMobile();

  // Set initial date range if not set
  const initialDateRange: DateRange = customDateRange || {
    startDate: new Date(new Date().setDate(1)), // First day of current month
    endDate: endOfDay(new Date()), // Today
  };

  // Handle time filter button click
  const handleTimeFilterClick = (filter: TimeFilter) => {
    setTimeFilter(filter);
  };

  // Handle date range change
  const handleDateRangeChange = (range: DateRange) => {
    console.log("DateRange changed:", range);
    // Ensure both dates are set
    if (range.startDate && range.endDate) {
      setCustomDateRange(range);
      setTimeFilter("custom");
    }
  };

  // Mobile view: 2x2 grid for time period buttons
  if (isMobile) {
    return (
      <div className={cn("flex flex-col gap-3", className)}>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTimeFilterClick("week")}
            className={cn(
              "bg-white/5 border-white/10 hover:bg-white/10 w-full",
              timeFilter === "week" && "border-market-teal text-market-teal"
            )}
          >
            This Week
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTimeFilterClick("month")}
            className={cn(
              "bg-white/5 border-white/10 hover:bg-white/10 w-full",
              timeFilter === "month" && "border-market-teal text-market-teal"
            )}
          >
            This Month
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTimeFilterClick("year")}
            className={cn(
              "bg-white/5 border-white/10 hover:bg-white/10 w-full",
              timeFilter === "year" && "border-market-teal text-market-teal"
            )}
          >
            This Year
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTimeFilterClick("all")}
            className={cn(
              "bg-white/5 border-white/10 hover:bg-white/10 w-full",
              timeFilter === "all" && "border-market-teal text-market-teal"
            )}
          >
            All Time
          </Button>
        </div>

        <DateRangePicker
          dateRange={initialDateRange}
          onDateRangeChange={handleDateRangeChange}
        />
      </div>
    );
  }

  // Desktop view remains unchanged
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleTimeFilterClick("week")}
        className={cn(
          "bg-white/5 border-white/10 hover:bg-white/10",
          timeFilter === "week" && "border-market-teal text-market-teal"
        )}
      >
        This Week
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleTimeFilterClick("month")}
        className={cn(
          "bg-white/5 border-white/10 hover:bg-white/10",
          timeFilter === "month" && "border-market-teal text-market-teal"
        )}
      >
        This Month
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleTimeFilterClick("year")}
        className={cn(
          "bg-white/5 border-white/10 hover:bg-white/10",
          timeFilter === "year" && "border-market-teal text-market-teal"
        )}
      >
        This Year
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleTimeFilterClick("all")}
        className={cn(
          "bg-white/5 border-white/10 hover:bg-white/10",
          timeFilter === "all" && "border-market-teal text-market-teal"
        )}
      >
        All Time
      </Button>

      <DateRangePicker
        dateRange={initialDateRange}
        onDateRangeChange={handleDateRangeChange}
      />
    </div>
  );
};

export default DateRangeSelector;
